//changed due to package upgrade
import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import AppTextField from '@projectK/components/AppTextField';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Box, alpha, MenuItem, Button, Grid, TextField, IconButton } from '@mui/material';
import AppInfoView from '@projectK/components/AppInfoView';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import PropTypes from 'prop-types';
import { CloseRounded } from '@mui/icons-material';
import { isParentRoleType } from '@projectK/context/RolesProvider';

const validationSchema = yup.object({
  first_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.firstName.noSpaces' />)
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.lastName.noSpaces' />)
    .required(<IntlMessages id='validation.lastNameRequired' />),
  role_id: yup
    .string()
    .required(<IntlMessages id='validation.userTypeRequired' />),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      () => <IntlMessages id='validation.invalidEmailFormat' />
    )
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
});

const customScrollbarStyle = {
  // Define the styles for the scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461', // Lighter black color
    borderRadius: '8px', // Curved border
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#666', // Lighter black color on hover
  },
};

const AddNewParentMember = ({ isOpen, onClose, fetchMembersData }) => {
  const { verbiageSettings } = useApplicationSettings();
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [roleOptions, setRoleOptions] = useState([]);
  useEffect(() => {
    fetchInstructorUsersData();
  }, []);
  const { user } = useAuthUser();
  const [openDialog, setOpenDialog] = useState(false);
  const fetchInstructorUsersData = async () => {
    try {
      const response = await jwtAxios.get(`/roles`);
      const { data } = response;
      setRoleOptions(data.list);
    } catch (error) {
      setRoleOptions([]);
    }
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    onClose();
  };
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
            minWidth: 300,
            maxHeight: 'calc(100vh - 200px)',
            overflow: 'auto',
            ...customScrollbarStyle,
          }}
        >
          <Formik
            validateOnChange={true}
            initialValues={{
              first_name: '',
              last_name: '',
              email: '',
              role_id: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              const { role_id, ...formData } = values;
              formData['is_parent'] = true;
              formData['roles'] = [role_id];
              setSubmitting(true);
              jwtAxios
                .post(`/users/${user.id}/addmember`, formData)
                .then((response) => {
                  setSubmitting(false);
                  handleCloseModalDialog();
                  fetchMembersData();
                  infoViewActionsContext.showMessage(
                    'Member added successfully!',
                  );
                })
                .catch((error) => {
                  infoViewActionsContext.fetchError(
                    error?.response?.data?.error,
                  );
                  setSubmitting(false);
                });
            }}
          >
            {({ handleChange, errors, touched }) => (
              <Form>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Box>
                <Typography
                  variant='h4'
                  sx={{
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  Add{' '}
                  {`${
                    verbiageSettings.account_member_alias || 'Parent Member'
                  }`}
                </Typography>
                </Box>
                 <Box>
                  <IconButton
                    size='small'
                    sx={{ fontWeight: 'bold', color: '#000000' }}
                    className='icon-btn'
                    onClick={handleDialogOpen}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>
                </Box>
                <AppGridContainer sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <AppGridContainer>
                            <Grid size={{ xs: 12, md: 12 }}>
                              <AppTextField
                                name='first_name'
                                variant='outlined'
                                sx={{
                                  width: '100%',
                                }}
                                label='First Name'
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }}>
                              <AppTextField
                                name='last_name'
                                variant='outlined'
                                sx={{
                                  width: '100%',
                                }}
                                label='Last Name'
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }}>
                              <AppTextField
                                name='email'
                                variant='outlined'
                                sx={{
                                  width: '100%',
                                }}
                                label='Email'
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }}>
                              <TextField
                                sx={{
                                  width: '100%',
                                }}
                                select
                                name='role_id'
                                error={
                                  touched.role_id && Boolean(errors.role_id)
                                }
                                helperText={touched.role_id && errors.role_id}
                                value={roleOptions.role_name}
                                label='Role Type'
                                onChange={handleChange}
                              >
                                {roleOptions
                                  .filter((pt) => isParentRoleType(pt))
                                  .map((pt) => (
                                    <MenuItem
                                      key={pt.id}
                                      value={pt.id}
                                      sx={{ textTransform: 'capitalize' }}
                                    >
                                      {pt.role_name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                          </AppGridContainer>
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  backgroundColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                    : alpha('#2196f3', 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              Submit
                            </Button>

                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  borderColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8)
                                    : alpha('#2196f3', 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
      <AppInfoView />
    </>
  );
};

export default AddNewParentMember;
AddNewParentMember.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fetchMembersData: PropTypes.func,
};
